import { useAxleMutation, useAxleQuery } from '@/fetching/apiHelpers';
import { organizationBasicSchema } from '@/fetching/sharedSchemas/basicSchemas';
import {
    qualificationBasicSchema,
    qualificationWithPermissionsSchema,
    sortQualifications,
} from '@/fetching/sharedSchemas/qualificationSchemas';
import _ from 'lodash';
import { z } from 'zod';

const qualificationsByOrgResponseSchema = z.object({
    qualifications_by_org: z.record(
        z.object({
            org: organizationBasicSchema,
            native_qualifications: z.array(qualificationWithPermissionsSchema),
            passthrough_qualifications: z.array(qualificationWithPermissionsSchema),
        }),
    ),
});

const qualificationsByOrgQuery = {
    queryKey: ['qualifications', 'all'],
    path: '/qualifications',
    responseSchema: qualificationsByOrgResponseSchema,
    transform: (data: z.infer<typeof qualificationsByOrgResponseSchema>) => ({
        qualificationsByOrg: _.mapValues(data.qualifications_by_org, (value) => ({
            org: value.org,
            native_qualifications: sortQualifications(value.native_qualifications),
            passthrough_qualifications: sortQualifications(value.passthrough_qualifications),
        })),
    }),
};

// This is used to return a list of qualifications for whichever orgs
// the user has permissions for.  For QHP/Visit position qualification options,
//we should implement more refined endpoints that return those specific qualifications.
export const useListQualificationsByOrg = () =>
    useAxleQuery({ ...qualificationsByOrgQuery, suspense: true }).data;

// For callers that read the list while the page renders, rather than rendering
//  from it. `enabled` false skips the request entirely.
export const useListQualificationsByOrgWithoutSuspense = (enabled: boolean) =>
    useAxleQuery({ ...qualificationsByOrgQuery, suspense: false, enabled }).data;

export const useQualifications = () =>
    useAxleQuery({
        queryKey: ['qualifications', 'all'],
        path: '/qualifications',
        suspense: true,
        responseSchema: z.object({
            qualifications: z.array(qualificationBasicSchema),
        }),
    }).data.qualifications;

export const useCreateQualificationOptions = () =>
    useAxleQuery({
        queryKey: ['qualifications', 'create'],
        path: '/qualifications/create/options',
        suspense: true,
        responseSchema: z.object({
            org_options: z.array(organizationBasicSchema),
            code_max_length: z.number(),
        }),
        transform: (data) => ({
            orgOptions: data.org_options,
            codeMaxLength: data.code_max_length,
        }),
    }).data;

export const useCreateQualification = () =>
    useAxleMutation({
        axiosConfig: ({
            org_uuid,
            code,
            description,
        }: {
            org_uuid: string;
            code: string;
            description: string;
        }) => ({
            method: 'post',
            path: '/qualifications/create',
            data: {
                org_uuid,
                code,
                description,
            },
        }),
        softResetQueries: [
            ['qualifications'],
            ['professional'],
            ['professionals', 'options'],
            ['connections'],
            ['services'],
        ],
        defaultSuccessMessage: 'Created the qualification.',
    });

export const useEditQualificationOptions = (qualificationId: string) =>
    useAxleQuery({
        queryKey: ['qualifications', qualificationId],
        path: `/qualifications/edit/options`,
        params: {
            qualification_uuid: qualificationId,
        },
        suspense: true,
        responseSchema: z.object({
            code_max_length: z.number(),
        }),
        transform: (data) => ({
            codeMaxLength: data.code_max_length,
        }),
    }).data;

export const useEditQualification = (qualificationId: string) =>
    useAxleMutation({
        axiosConfig: ({ code, description }: { code: string; description: string }) => ({
            method: 'post',
            path: `/qualifications/edit`,
            data: {
                qualification_uuid: qualificationId,
                code,
                description,
            },
        }),
        softResetQueries: [
            ['qualifications'],
            ['professional'],
            ['professionals', 'options'],
            ['connections'],
            ['services'],
        ],
        defaultSuccessMessage: 'Edited the qualification.',
    });

export const useDeleteQualification = (qualificationId: string) =>
    useAxleMutation({
        axiosConfig: () => ({
            method: 'post',
            path: `/qualifications/delete`,
            data: {
                qualification_uuid: qualificationId,
            },
        }),
        softResetQueries: [
            ['qualifications'],
            ['professional'],
            ['professionals', 'options'],
            ['connections'],
            ['services'],
        ],
        defaultSuccessMessage: 'Deleted the qualification.',
    });
